import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { TableTopPanel } from 'components/TableTopPanel';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    BimChapterEnum,
    BimPriceLevelEnum,
    BimWorkOrderPriceData,
    ContractStatusEnum,
    ProjectStatusEnum,
} from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useHighLightRulesWithRequestData } from 'pages/Project/Common/hooks/useHighLightRulesWithRequestData';
import { getCheckedIdsArr } from 'utils';

import type { GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { ForwardedRef, MutableRefObject } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';

import { destroyWorkOrderFilters } from '../../../UerProject/WorkOrder/WorkOrderPrice/hooks';
import { BimWorkOrderPriceRowDetails } from '../../BimCommonComponents/BimWorkOrderPriceRowDetails';
import { BimWorkOrderActsKc2 } from '../BimWorkOrderActsKc2';
import { BimWorkOrderPriceAnalytics } from '../BimWorkOrderPriceAnalytics';
import { BimWorkOrderPriceRevisions } from '../BimWorkOrderPriceRevisions/BimWorkOrderPriceRevisions';
import { useBimWorkOrderTotalsRow } from '../hooks';
import { useBimWorkOrderPriceStates } from '../hooks/useBimWorkOrderPriceStates';
import type { BimWorkOrderRowTypeEnum } from '../types';
import { BimWorkOrderRowDetailsType } from '../types';

import type { IBimWorkOrderRowDetails } from './cellRenderers';
import { getBimWorkOrderRowClass } from './cellRenderers';
import {
    useBimWorkOrderDeletePrices,
    useBimWorkOrderPrice,
    useBimWorkOrderPriceDetails,
    useBimWorkOrderPriceRevisionsFilter,
    useWorkOrderColumns,
} from './hooks';

import Style from './BimWorkOrderPriceTable.module.css';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

interface IBimWorkOrderPriceTable {
    workOrderId: number;
    workOrderRootObjectId: number | undefined;
    chapter: BimChapterEnum | undefined;
    constructionTypeId: number | undefined;
    totalChapterDetailsRowOpenOuter: { rowType: BimWorkOrderRowTypeEnum.TotalsString; data: null } | null;
    toggleTotalChapterDetailsOuter: () => void;
    makeRefreshTable: () => void;
    hasInProgress: boolean;
    isFrameContract: boolean;
    includeRows?: boolean;
    workOrderStatus?: WorkOrderStatusEnum;
    resetCheckedRowsRef: MutableRefObject<(() => void) | undefined>;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    refreshProject: () => void;
    priceLevel: BimPriceLevelEnum | undefined;
}

export const BimWorkOrderPriceTable = forwardRef(
    (props: IBimWorkOrderPriceTable, ref: ForwardedRef<AgGridReact<any>>) => {
        const {
            workOrderRootObjectId,
            workOrderId,
            chapter,
            includeRows = false,
            constructionTypeId,
            totalChapterDetailsRowOpenOuter,
            toggleTotalChapterDetailsOuter,
            makeRefreshTable: makeRefresh,
            workOrderStatus,
            resetCheckedRowsRef,
            contractStatus,
            orderRules,
            statusProject,
            hasInProgress,
            isFrameContract,
            refreshProject,
            priceLevel,
        } = props;

        const tableKey = useMemo(
            () => `${chapter}_${workOrderRootObjectId}-${priceLevel}`,
            [chapter, workOrderRootObjectId, priceLevel],
        );

        const {
            bimWorkOrderPriceStatesData: { hasExcessCompletedTotalSMR },
            bimWorkOrderPriceStatesMakeRequest,
        } = useBimWorkOrderPriceStates(chapter, workOrderRootObjectId);

        const makeRefreshTable = useCallback(() => {
            makeRefresh();
            bimWorkOrderPriceStatesMakeRequest();
        }, [bimWorkOrderPriceStatesMakeRequest, makeRefresh]);

        const {
            fullSetColumns,
            detailsColumnsByType,
            detailsTitle,
            closeDetailsModalAndCleanChecked,
            checkedPriceRowForDetails,
            fullSetSettingsColumns,
            onFullSetTableSettingsConfirm,
            isFullSetColumnVisible,
            resetCheckedRows,
            checkedRows,
            filterValues,
            regularDetailsFilterValues,
            isTotalDetails,
        } = useWorkOrderColumns({
            workOrderRootObjectId,
            workOrderId,
            chapter,
            totalChapterDetailsRowOpenOuter,
            toggleTotalChapterDetailsOuter,
            makeRefreshTable,
            workOrderStatus,
            contractStatus,
            hasExcessCompletedTotalSMR,
            orderRules,
            statusProject,
            hasInProgress,
            isFrameContract,
        });

        const { bimPriceListDataSource, priceListCounter, fetchedItemsCounter } = useBimWorkOrderPrice({
            workOrderId,
            workOrderRootObjectId,
            chapter,
            includeRows,
            filterValues,
            priceLevel,
        });

        const {
            filterValues: revisionsFilterValues,
            changeFilterHandler: changeRevisionsFilterHandler,
            clearFilter: clearRevisionsFilter,
        } = useBimWorkOrderPriceRevisionsFilter();

        const { detailsData, isDetailsLoading, detailsBaseRow } = useBimWorkOrderPriceDetails({
            checkedPriceRowForDetails,
            chapter,
            workOrderObjectId: workOrderRootObjectId,
            constructionTypeId,
            workOrderId,
            totalChapterDetailsRowOpenOuter,
            regularDetailsFilterValues,
            priceLevel,
        });

        const { rowTotalsData } = useBimWorkOrderTotalsRow({
            chapter,
            constructionTypeId,
            workOrderId,
            workOrderObjectId: workOrderRootObjectId,
            priceLevel,
        });

        const memoizedRevisionsRequestIds = useMemo(
            () => ['bimWorkOrderPriceRevisionCodeFilter', 'bimWorkOrderPriceRevisionNameFilter'],
            [],
        );

        const [resetRevisionsFiltersData] = useResetMultipleStoreData({
            requestIds: memoizedRevisionsRequestIds,
        });

        const { handleDelete, deleteError, deleteStatus, deleteSetData, deleteData, onSuccess } =
            useBimWorkOrderDeletePrices({
                workOrderId,
                workOrderPriceIds: getCheckedIdsArr(checkedRows),
                resetCheckedRows,
                refreshTable: makeRefreshTable,
                workOrderStatus,
            });

        const onSuccessDeleTe = () => {
            onSuccess();
            refreshProject();
        };

        useImperativeHandle(resetCheckedRowsRef, () => resetCheckedRows, [resetCheckedRows]);

        useEffect(
            () => () => {
                resetCheckedRows();
            },
            [resetCheckedRows, workOrderId, workOrderRootObjectId, chapter],
        );

        useEffect(() => {
            if (typeof ref !== 'function' && ref?.current) {
                if (!priceListCounter) {
                    ref?.current?.api?.showNoRowsOverlay();
                } else {
                    ref?.current?.api?.hideOverlay();
                }
            }
        }, [priceListCounter, ref]);

        const onGridReady = useCallback((event: GridReadyEvent) => {
            event.api.showNoRowsOverlay();
        }, []);

        const onActsClose = useCallback(() => {
            closeDetailsModalAndCleanChecked();
            makeRefreshTable();
        }, [closeDetailsModalAndCleanChecked, makeRefreshTable]);

        const onRevisionsClose = useCallback(() => {
            closeDetailsModalAndCleanChecked();
            clearRevisionsFilter();
            resetRevisionsFiltersData();
        }, [clearRevisionsFilter, closeDetailsModalAndCleanChecked, resetRevisionsFiltersData]);

        const isShowDetails = useMemo(
            () =>
                (checkedPriceRowForDetails &&
                    checkedPriceRowForDetails.detailsType === BimWorkOrderRowDetailsType.Details) ||
                totalChapterDetailsRowOpenOuter,
            [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
        );

        const isShowHistoryDetails = useMemo(
            () =>
                checkedPriceRowForDetails?.detailsType &&
                [BimWorkOrderRowDetailsType.Update, BimWorkOrderRowDetailsType.Revision].includes(
                    checkedPriceRowForDetails?.detailsType,
                ),
            [checkedPriceRowForDetails],
        );

        const isShowActsKc2 = useMemo(
            () =>
                (checkedPriceRowForDetails &&
                    checkedPriceRowForDetails.detailsType === BimWorkOrderRowDetailsType.ActsKc2) ||
                totalChapterDetailsRowOpenOuter,
            [checkedPriceRowForDetails, totalChapterDetailsRowOpenOuter],
        );
        const isShowAnalytics = useMemo(
            () =>
                checkedPriceRowForDetails &&
                checkedPriceRowForDetails.detailsType === BimWorkOrderRowDetailsType.Analytics,
            [checkedPriceRowForDetails],
        );

        const { textHeader, textConfirmation } = useMemo(
            () =>
                workOrderStatus === WorkOrderStatusEnum.InProgress
                    ? {
                          textHeader: 'Удаление сметы',
                          textConfirmation: 'Вы уверены, что хотите удалить актуальную версию сметы?',
                      }
                    : {},
            [workOrderStatus],
        );

        const refreshKey = useMemo(() => `${workOrderId}_${workOrderStatus}`, [workOrderId, workOrderStatus]);

        const { rules, rowClickHandler } = useHighLightRulesWithRequestData({
            requestIds: useMemo(() => ['bimUploadPriceFiles', 'bimUploadPriceActs'], []),
            dataKeyAccessor: 'itemIds',
            baseRules: getBimWorkOrderRowClass,
            gridApiRef: (ref as MutableRefObject<any>)?.current,
            refreshKey,
        });

        const handleRowClick = useCallback(
            (e: RowClickedEvent) => {
                rowClickHandler(e?.data?.id);
            },
            [rowClickHandler],
        );

        const gridOptions = {
            onRowClicked: handleRowClick,
        };

        const tableApi = useMemo(() => (ref && 'current' in ref ? ref.current : undefined), [ref]);

        useEffect(() => {
            destroyWorkOrderFilters({
                api: (ref as any)?.current?.api,
                columns: ['object.name', 'planValue.code', 'planValue.name'],
            });
        }, [workOrderRootObjectId, workOrderId, chapter, ref]);

        return (
            <>
                <TableTopPanel
                    checkedRowsArr={getCheckedIdsArr(checkedRows)}
                    resetCheckedRows={resetCheckedRows}
                    totalItems={priceListCounter}
                    fetchedItems={fetchedItemsCounter}
                    optionalColumns={fullSetSettingsColumns}
                    handleTableSettingsConfirm={onFullSetTableSettingsConfirm}
                    isColumnVisible={isFullSetColumnVisible}
                    areChildrenOnLeftSide
                    showPinColumn
                    tableApi={tableApi}
                >
                    <ButtonDeleteIds
                        deleteData={deleteData}
                        deleteStatus={deleteStatus}
                        deleteError={deleteError}
                        deleteSetData={deleteSetData}
                        handleDelete={handleDelete}
                        onSuccess={onSuccessDeleTe}
                        textHeader={textHeader}
                        textConfirmation={textConfirmation}
                        style={{ marginRight: 10 }}
                    />
                </TableTopPanel>

                <div className={`ag-theme-alpine BimWorkOrderPrice ${Style.bimWorkOrderPrice} customHeaderColor`}>
                    <AgGridTable
                        datasource={bimPriceListDataSource}
                        columnDefs={fullSetColumns}
                        overlayNoRowsTemplate="Для отображения данных загрузите сметы"
                        rowModelType="infinite"
                        tooltipShowDelay={200}
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        defaultColDef={defaultColDef}
                        pinnedBottomRowData={rowTotalsData}
                        rowClassRules={rules}
                        onGridReady={onGridReady}
                        gridOptions={gridOptions}
                        ref={ref}
                        context={{
                            tableUniqueId: `${chapter}_${workOrderRootObjectId}`,
                        }}
                        key={tableKey}
                    />
                </div>
                {isShowDetails && (
                    <BimWorkOrderPriceRowDetails
                        modalTitle={detailsTitle}
                        baseRow={detailsBaseRow}
                        data={detailsData}
                        columns={detailsColumnsByType}
                        onClose={closeDetailsModalAndCleanChecked}
                        isDetailsLoading={isDetailsLoading}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        rowClassRules={getBimWorkOrderRowClass}
                        suppressDragLeaveHidesColumns={!isTotalDetails}
                        priceLevel={isTotalDetails ? undefined : priceLevel}
                    />
                )}
                {isShowHistoryDetails && checkedPriceRowForDetails && (
                    <BimWorkOrderPriceRevisions
                        onClose={onRevisionsClose}
                        columns={fullSetColumns}
                        baseRow={detailsBaseRow}
                        filterValues={revisionsFilterValues}
                        changeFilterHandler={changeRevisionsFilterHandler}
                        selectedRow={checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>}
                        rowClassRules={getBimWorkOrderRowClass}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        priceLevel={priceLevel}
                    />
                )}
                {isShowActsKc2 && checkedPriceRowForDetails && (
                    <BimWorkOrderActsKc2
                        onClose={onActsClose}
                        baseRow={detailsBaseRow}
                        selectedRow={checkedPriceRowForDetails as IBimWorkOrderRowDetails<BimWorkOrderPriceData>}
                        rowClassRules={getBimWorkOrderRowClass}
                        defaultColDef={defaultColDef}
                        styleClass="BimWorkOrderPriceDetails customHeaderColor"
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                        statusProject={statusProject}
                        orderRules={orderRules}
                        priceLevel={priceLevel}
                    />
                )}
                {isShowAnalytics && checkedPriceRowForDetails && (
                    <BimWorkOrderPriceAnalytics
                        onClose={closeDetailsModalAndCleanChecked}
                        baseRow={checkedPriceRowForDetails as any}
                        priceLevel={priceLevel}
                    />
                )}
            </>
        );
    },
);

BimWorkOrderPriceTable.displayName = 'BimWorkOrderPriceTable';
